/**
 * Hash-bound user review declaration for Paper2Ink.
 * Binds user review confirmation to the exact cryptographic content hash of document objects.
 */

import type { CIMDocument, UserDeclaration } from '../editor/model.ts';

export async function computeDocumentContentHash(doc: CIMDocument): Promise<string> {
  const payload = JSON.stringify({
    page: doc.page,
    objects: doc.objects,
    provenance: doc.provenance,
  });

  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle) {
    const data = new TextEncoder().encode(payload);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  const { createHash } = await import('node:crypto');
  return createHash('sha256').update(payload).digest('hex');
}

export async function createUserDeclaration(doc: CIMDocument): Promise<UserDeclaration> {
  const hash = await computeDocumentContentHash(doc);
  return {
    confirmed: true,
    declaration_time: new Date().toISOString(),
    content_hash: hash,
  };
}

export async function verifyUserDeclaration(doc: CIMDocument): Promise<boolean> {
  const decl = doc.review.user_declaration;
  if (!decl || !decl.confirmed || !decl.content_hash) {
    return false;
  }
  const currentHash = await computeDocumentContentHash(doc);
  return decl.content_hash === currentHash;
}
